import React, { Children, isValidElement, ReactNode, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  alpha,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { MenuBook, Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getResearchArticle, getResearchArticles, ResearchArticle } from '../services/api';

const READING_WIDTH = 760;

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

// Plain text of a React node tree (for heading anchors and title matching).
const textOf = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return Children.toArray(node).map(textOf).join('');
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const useArticles = () =>
  useQuery<ResearchArticle[], Error>({
    queryKey: ['researchArticles'],
    queryFn: getResearchArticles,
    staleTime: 60 * 60 * 1000,
  });

const ArticleMeta: React.FC<{ article: ResearchArticle }> = ({ article }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    <Typography variant="body2" color="text.secondary">
      {formatDate(article.date)} · {article.readingMinutes} min read
    </Typography>
    {article.tags.map((t) => (
      <Chip key={t} label={t} size="small" variant="outlined" />
    ))}
  </Stack>
);

// Sidebar sections, in order. Articles set theirs with `section:` in their front matter.
const SECTIONS: { name: string; blurb: string }[] = [
  { name: 'Guides', blurb: 'Practical how-tos for using the models and tools to find value.' },
  { name: 'How it works', blurb: 'The methods behind the simulator, track similarity and parlay pricing.' },
  { name: 'Analysis & case studies', blurb: 'Backtests, calibration and deep dives, with the actual numbers.' },
];

const groupBySection = (articles: ResearchArticle[]) =>
  SECTIONS.map((s) => ({ ...s, articles: articles.filter((a) => a.section === s.name) }))
    .concat(
      // Anything without a known section still shows up.
      [{ name: 'More', blurb: '', articles: articles.filter((a) => !SECTIONS.some((s) => s.name === a.section)) }],
    )
    .filter((s) => s.articles.length > 0);

/** Docs-style sidebar: sections with their articles, a filter box, and the current page highlighted. */
const DocsSidebar: React.FC<{ articles: ResearchArticle[]; activeSlug?: string; onNavigate?: () => void }> = ({
  articles,
  activeSlug,
  onNavigate,
}) => {
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const shown = q
    ? articles.filter((a) => `${a.title} ${a.summary} ${a.tags.join(' ')}`.toLowerCase().includes(q))
    : articles;
  const itemSx = (active: boolean) => ({
    display: 'block',
    position: 'relative' as const,
    px: 1.5,
    py: 0.75,
    borderRadius: 1.5,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    textDecoration: 'none',
    color: active ? 'primary.main' : 'text.secondary',
    fontWeight: active ? 600 : 400,
    bgcolor: (t: Theme) => (active ? alpha(t.palette.primary.main, 0.12) : 'transparent'),
    '&:hover': {
      color: active ? 'primary.main' : 'text.primary',
      bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
    },
    ...(active && {
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 6,
        bottom: 6,
        width: 3,
        borderRadius: 2,
        bgcolor: 'primary.main',
      },
    }),
  });
  return (
    <Box component="nav" aria-label="Research">
      <TextField
        size="small"
        fullWidth
        placeholder="Filter articles"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
      <Box component={RouterLink} to="/research" onClick={onNavigate} sx={itemSx(!activeSlug)}>
        Overview
      </Box>
      {groupBySection(shown).map((sec) => (
        <Box key={sec.name} sx={{ mt: 2.5 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              px: 1.5,
              mb: 0.5,
              lineHeight: 1.6,
              color: 'text.primary',
              fontWeight: 700,
              letterSpacing: '0.08em',
            }}
          >
            {sec.name}
          </Typography>
          {sec.articles.map((a) => (
            <Box
              key={a.slug}
              component={RouterLink}
              to={`/research/${a.slug}`}
              onClick={onNavigate}
              sx={itemSx(a.slug === activeSlug)}
            >
              {a.title}
            </Box>
          ))}
        </Box>
      ))}
      {shown.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, mt: 2 }}>
          No articles match “{filter}”.
        </Typography>
      )}
    </Box>
  );
};

/** Page frame shared by the docs home and articles: sticky sidebar (a drawer on small screens), content, outline. */
const DocsLayout: React.FC<{ activeSlug?: string; outline?: ReactNode; children: ReactNode }> = ({
  activeSlug,
  outline,
  children,
}) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const [drawer, setDrawer] = useState(false);
  const { data: articles = [] } = useArticles();
  const sidebar = <DocsSidebar articles={articles} activeSlug={activeSlug} onNavigate={() => setDrawer(false)} />;
  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 8 }}>
      {!desktop && (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<MenuBook />}
            onClick={() => setDrawer(true)}
            sx={{ mb: 2 }}
          >
            Browse research
          </Button>
          <Drawer
            anchor="left"
            open={drawer}
            onClose={() => setDrawer(false)}
            PaperProps={{ sx: { width: 300, p: 2 } }}
          >
            {sidebar}
          </Drawer>
        </>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: desktop
            ? wide && outline
              ? `260px minmax(0, ${READING_WIDTH}px) 220px`
              : `260px minmax(0, 1fr)`
            : 'minmax(0, 1fr)',
          columnGap: { md: 5, lg: 6 },
          justifyContent: 'center',
        }}
      >
        {desktop && (
          <Box
            sx={{
              position: 'sticky',
              top: 24,
              alignSelf: 'start',
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
              pr: 2,
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            {sidebar}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        {desktop && wide && outline && <Box sx={{ position: 'sticky', top: 24, alignSelf: 'start' }}>{outline}</Box>}
      </Box>
    </Container>
  );
};

/** Docs home: what the section covers, guides to start with, and every section's articles. */
const ResearchIndex: React.FC = () => {
  const { data, isLoading, error } = useArticles();
  const sections = groupBySection(data ?? []);
  const guides = sections.find((s) => s.name === 'Guides');
  return (
    <DocsLayout>
      <Box sx={{ maxWidth: 880 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '2rem', sm: '2.6rem' } }}
        >
          Research
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.75, fontSize: '1.08rem' }}>
          How the models behind Left Turn Betting work, how to use them, and what we've learned building them, with the
          methods and the actual results of every analysis.
        </Typography>
        {isLoading && <CircularProgress size={28} />}
        {error && <Alert severity="error">{error.message}</Alert>}

        {guides && (
          <>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              Start here
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mt: 1,
                mb: 5,
              }}
            >
              {guides.articles.map((a) => (
                <Paper
                  key={a.slug}
                  component={RouterLink}
                  to={`/research/${a.slug}`}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 2,
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 0.75 }}>
                    {a.title} →
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {a.summary}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </>
        )}

        {sections
          .filter((s) => s.name !== 'Guides')
          .map((sec) => (
            <Box key={sec.name} sx={{ mb: 5 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 650, mb: 0.5 }}>
                {sec.name}
              </Typography>
              {sec.blurb && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {sec.blurb}
                </Typography>
              )}
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                {sec.articles.map((a) => (
                  <Box
                    key={a.slug}
                    component={RouterLink}
                    to={`/research/${a.slug}`}
                    sx={{
                      display: 'block',
                      py: 1.75,
                      px: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover .title': { color: 'primary.main' },
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography
                      className="title"
                      variant="subtitle1"
                      sx={{ fontWeight: 600, transition: 'color 0.15s' }}
                    >
                      {a.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 0.25 }}>
                      {a.summary}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {a.readingMinutes} min read · {formatDate(a.date)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
      </Box>
    </DocsLayout>
  );
};

/** Markdown renderer with publication typography; italic article titles become links to those articles. */
const markdownComponents = (titleToSlug: Map<string, string>, mode: 'light' | 'dark'): Components => {
  const heading =
    (variant: 'h4' | 'h5' | 'h6', component: 'h2' | 'h3' | 'h4', mt: number) =>
    ({ children }: { children?: ReactNode }) => (
      <Typography
        id={slugify(textOf(children))}
        variant={variant}
        component={component}
        sx={{ fontWeight: 650, mt, mb: 1.5, scrollMarginTop: 80 }}
      >
        {children}
      </Typography>
    );
  return {
    h1: heading('h4', 'h2', 5),
    h2: heading('h5', 'h2', 5),
    h3: heading('h6', 'h3', 3.5),
    h4: heading('h6', 'h4', 3),
    p: ({ children }) => (
      <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.75 }}>
        {children}
      </Typography>
    ),
    li: ({ children }) => (
      <Typography variant="body1" component="li" sx={{ mb: 0.75, lineHeight: 1.7 }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0 }}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 3, mb: 2, mt: 0 }}>
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      // Used for formulas: set apart, monospace-free but emphasized.
      <Box
        sx={{
          my: 2.5,
          px: 2.5,
          py: 1.5,
          borderLeft: 3,
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
          borderRadius: 1,
          '& p': { mb: 0, fontSize: '1.05rem' },
        }}
      >
        {children}
      </Box>
    ),
    em: ({ children }) => {
      // Normalize whitespace: a reference may wrap across lines in the Markdown source.
      const slug = titleToSlug.get(textOf(children).replace(/\s+/g, ' ').trim().toLowerCase());
      return slug ? (
        <Link component={RouterLink} to={`/research/${slug}`} underline="hover" sx={{ fontStyle: 'italic' }}>
          {children}
        </Link>
      ) : (
        <em>{children}</em>
      );
    },
    // Figures: a "{theme}" placeholder in the path picks the light or dark version to match the site theme.
    img: ({ src, alt }) => (
      <Box
        component="img"
        src={String(src ?? '').replace(/\{theme\}|%7Btheme%7D/gi, mode)}
        alt={alt ?? ''}
        loading="lazy"
        sx={{ display: 'block', maxWidth: '100%', height: 'auto', mx: 'auto', my: 2 }}
      />
    ),
    a: ({ href, children }) =>
      // Links to other pages of the app (e.g. another article) stay in the tab; external links open a new one.
      href?.startsWith('/') ? (
        <Link component={RouterLink} to={href} underline="hover">
          {children}
        </Link>
      ) : (
        <Link href={href ?? '#'} target="_blank" rel="noopener noreferrer" underline="hover">
          {children}
        </Link>
      ),
    code: ({ children }) => (
      <Box
        component="code"
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.88em',
          bgcolor: 'action.selected',
          px: 0.5,
          borderRadius: 0.5,
        }}
      >
        {children}
      </Box>
    ),
    // Fenced code blocks (formulas, pseudocode): a scrollable block; the inner <code> drops its inline styling.
    pre: ({ children }) => (
      <Box
        component="pre"
        sx={{
          my: 2.5,
          p: 2,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          overflowX: 'auto',
          fontSize: '0.88rem',
          lineHeight: 1.6,
          '& code': { bgcolor: 'transparent', px: 0, fontSize: 'inherit', whiteSpace: 'pre' },
        }}
      >
        {children}
      </Box>
    ),
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.92rem',
            fontVariantNumeric: 'tabular-nums',
            '& th, & td': { px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', verticalAlign: 'top' },
            '& th': { textAlign: 'left', fontWeight: 650, bgcolor: 'action.hover', whiteSpace: 'nowrap' },
            '& tr:last-of-type td': { borderBottom: 0 },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    hr: () => <Box component="hr" sx={{ my: 4, border: 0, borderTop: 1, borderColor: 'divider' }} />,
  };
};

/** "On this page" outline with the heading in view highlighted. */
const Outline: React.FC<{ items: { title: string; id: string }[] }> = ({ items }) => {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const els = items.map((i) => document.getElementById(i.id)).filter((e): e is HTMLElement => !!e);
    if (els.length === 0 || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px' },
    );
    els.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, [items]);
  return (
    <Box component="nav" aria-label="On this page" sx={{ pt: 1 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
        On this page
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5, borderLeft: 1, borderColor: 'divider' }}>
        {items.map((h) => (
          <Link
            key={h.id}
            href={`#${h.id}`}
            underline="none"
            variant="body2"
            sx={{
              pl: 1.75,
              py: 0.4,
              ml: '-1px',
              borderLeft: 2,
              borderColor: active === h.id ? 'primary.main' : 'transparent',
              color: active === h.id ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            {h.title}
          </Link>
        ))}
      </Stack>
    </Box>
  );
};

/** Previous / next article (in sidebar order) at the bottom of an article. */
const PrevNext: React.FC<{ prev?: ResearchArticle; next?: ResearchArticle }> = ({ prev, next }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 6 }}>
    {[prev, next].map((a, i) =>
      a ? (
        <Paper
          key={a.slug}
          variant="outlined"
          component={RouterLink}
          to={`/research/${a.slug}`}
          sx={{
            p: 2,
            borderRadius: 2,
            textDecoration: 'none',
            color: 'inherit',
            textAlign: i === 0 ? 'left' : 'right',
            gridColumn: { sm: i === 0 ? 1 : 2 },
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {i === 0 ? '← Previous' : 'Next →'}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {a.title}
          </Typography>
        </Paper>
      ) : (
        <Box key={i} />
      ),
    )}
  </Box>
);

/** One article. */
const ResearchArticleView: React.FC<{ slug: string }> = ({ slug }) => {
  const theme = useTheme();
  const list = useArticles();
  const {
    data: article,
    isLoading,
    error,
  } = useQuery<ResearchArticle, Error>({
    queryKey: ['researchArticle', slug],
    queryFn: () => getResearchArticle(slug),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const titleToSlug = useMemo(
    () => new Map((list.data ?? []).map((a) => [a.title.toLowerCase(), a.slug])),
    [list.data],
  );
  const components = useMemo(
    () => markdownComponents(titleToSlug, theme.palette.mode),
    [titleToSlug, theme.palette.mode],
  );
  // Outline from the article's "## " headings.
  const toc = useMemo(
    () =>
      (article?.body ?? '')
        .split('\n')
        .filter((l) => /^## /.test(l))
        .map((l) => l.replace(/^## /, '').trim())
        .map((t) => ({ title: t, id: slugify(t) })),
    [article?.body],
  );
  // Previous / next follow the sidebar's order (by section, then within it).
  const ordered = useMemo(() => groupBySection(list.data ?? []).flatMap((s) => s.articles), [list.data]);
  const idx = ordered.findIndex((a) => a.slug === slug);
  const prev = idx > 0 ? ordered[idx - 1] : undefined;
  const next = idx >= 0 ? ordered[idx + 1] : undefined;
  const section = ordered[idx]?.section;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  return (
    <DocsLayout activeSlug={slug} outline={toc.length > 1 ? <Outline items={toc} /> : undefined}>
      {isLoading && <CircularProgress size={28} />}
      {(error || (!isLoading && !article)) && <Alert severity="error">{error?.message ?? 'Article not found'}</Alert>}
      {article && (
        <Box component="article">
          <Breadcrumbs sx={{ mb: 2, '& .MuiBreadcrumbs-li': { fontSize: '0.875rem' } }}>
            <Link component={RouterLink} to="/research" underline="hover" color="inherit">
              Research
            </Link>
            {section && (
              <Typography color="text.secondary" sx={{ fontSize: 'inherit' }}>
                {section}
              </Typography>
            )}
          </Breadcrumbs>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.9rem', sm: '2.4rem' } }}
          >
            {article.title}
          </Typography>
          <ArticleMeta article={article} />
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ mt: 2.5, mb: 1, fontWeight: 400, lineHeight: 1.6, fontSize: '1.12rem' }}
          >
            {article.summary}
          </Typography>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {article.body ?? ''}
          </ReactMarkdown>
          <PrevNext prev={prev} next={next} />
        </Box>
      )}
    </DocsLayout>
  );
};

/** Research section: /research (docs home) and /research/:slug (article). */
export const Research: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  return slug ? <ResearchArticleView slug={slug} /> : <ResearchIndex />;
};
